'use client';

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { HELP_T, type HelpLocale } from '../_components/help-header-i18n';
import { localizeHelpHref } from '../_components/help-routing';

/**
 * Thin client wrapper over `react-markdown`. We use Tailwind utility classes
 * on each rendered element rather than pulling the `@tailwindcss/typography`
 * plugin — keeps the bundle smaller and the palette consistent with EZDocu.
 */
export function HelpArticleRenderer({
  markdown,
  locale = 'en',
}: {
  markdown: string;
  /** Idioma de la URL: prefija los enlaces `/help/…` del CMS y traduce el vídeo. */
  locale?: HelpLocale;
}) {
  const t = HELP_T[locale] ?? HELP_T.en;
  return (
    <div className="mt-6 text-[17px] leading-relaxed text-gray-800">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...props }) => (
            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-2" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h4 className="text-lg font-semibold text-gray-900 mt-6 mb-2" {...props} />
          ),
          p: ({ node, ...props }) => <p className="my-3" {...props} />,
          ul: ({ node, ...props }) => (
            <ul className="my-3 list-disc list-outside pl-6 space-y-1" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="my-3 list-decimal list-outside pl-6 space-y-1" {...props} />
          ),
          li: ({ node, ...props }) => <li className="pl-1" {...props} />,
          a: ({ node, href, ...props }) => (
            <a
              className="text-purple-700 underline underline-offset-2 hover:text-purple-800"
              href={localizeHelpHref(href, locale)}
              {...props}
            />
          ),
          strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          code: ({ node, className, ...props }) => {
            const isInline = !className;
            return isInline ? (
              <code
                className="font-mono text-[14px] bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200"
                {...props}
              />
            ) : (
              <code className={className} {...props} />
            );
          },
          pre: ({ node, ...props }) => (
            <pre className="my-4 p-4 rounded-lg bg-gray-900 text-gray-100 overflow-x-auto text-sm" {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className="my-4 border-l-4 border-purple-300 pl-4 text-gray-700 italic"
              {...props}
            />
          ),
          hr: () => <hr className="my-8 border-gray-200" />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border border-gray-200 rounded-lg" {...props} />
            </div>
          ),
          th: ({ node, ...props }) => (
            <th className="text-left bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="px-3 py-2 border-b border-gray-100" {...props} />
          ),
          img: ({ node, src, alt, title, ...props }) => {
            // Overload markdown's image syntax to support branded video
            // embeds. Se detecta el vídeo por dos vías, porque una no basta:
            //  1) la extensión del archivo (.mp4/.webm/.mov), y
            //  2) el texto del alt cuando la extensión miente. Los vídeos de
            //     ayuda llegan del callcenter guardados en S3 con nombre .png
            //     aunque su contenido es video/mp4 (el servidor devuelve el
            //     Content-Type correcto, así que <video> los reproduce igual);
            //     como su alt siempre dice «Paso a paso en vídeo» / «walkthrough»
            //     / «Anleitung», ese texto es la señal fiable de que NO es una
            //     imagen. Sin esto salían como <img> roto.
            // El `title` (segundo arg en `![alt](url "title")`) es el póster.
            const altText = typeof alt === 'string' ? alt : '';
            const esVideoPorNombre = typeof src === 'string' && /\.(mp4|webm|mov)(\?|$)/i.test(src);
            const esVideoPorAlt = /\b(v[íi]deo|video|walkthrough|anleitung|passo a passo|paso a paso|étape par étape)\b/i.test(altText);
            if (typeof src === 'string' && (esVideoPorNombre || esVideoPorAlt)) {
              const poster = typeof title === 'string' ? title : undefined;
              return (
                <BrandedHelpVideo
                  src={src}
                  poster={poster}
                  title={altText || undefined}
                  label={t.videoTutorial}
                />
              );
            }
            return <img className="my-6 rounded-lg border border-gray-200 max-w-full" src={src} alt={alt} title={title} {...props} />;
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Branded video embed for help articles. Mirrors the purple chrome of the
 * widget's BrandedVideoCard so users get the same visual signal on the help
 * page and in the chat. Native <video> — no player deps. The header strip
 * lives OUTSIDE the 16:9 frame to avoid covering the speaker.
 */
function BrandedHelpVideo({
  src,
  poster,
  title,
  label,
}: {
  src: string;
  poster?: string;
  title?: string;
  label: string;
}) {
  // Match the wrapper's aspect-ratio to the actual video instead of
  // hard-coding 16:9. We start at 16:9 as a placeholder so the layout
  // doesn't pop in at 0px while metadata loads, then snap to the real
  // ratio once we know the intrinsic dimensions — no letterbox bars,
  // no crop. Tutorials recorded on a Mac (16:10) or a phone (9:16) all
  // sit cleanly inside the purple-branded frame.
  //
  // Cap the height at `max-h-[80vh]` so a portrait phone clip can't
  // shove the rest of the article off-screen.
  const [aspectRatio, setAspectRatio] = useState<string>('16 / 9');

  return (
    <div className="my-6 rounded-2xl border border-purple-200 overflow-hidden bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05),0_12px_28px_-12px_rgba(147,51,234,0.3)]">
      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-white border-b border-purple-100 text-[13px] font-semibold text-purple-700">
        <span className="inline-block w-4 h-4 rounded-sm bg-gradient-to-br from-purple-600 to-purple-400 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.4)]" />
        <span>EZDocuAI</span>
        <span className="text-purple-400 font-medium text-[12px]">· {label}</span>
      </div>
      <div
        className="relative w-full max-h-[80vh] bg-slate-950"
        style={{ aspectRatio }}
      >
        <video
          src={src}
          poster={poster}
          controls
          playsInline
          preload="metadata"
          controlsList="nodownload noremoteplayback"
          onContextMenu={(e) => e.preventDefault()}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (v.videoWidth > 0 && v.videoHeight > 0) {
              setAspectRatio(`${v.videoWidth} / ${v.videoHeight}`);
            }
          }}
          className="absolute inset-0 w-full h-full object-contain bg-slate-950"
        />
      </div>
      {title ? (
        <div className="px-4 py-2.5 text-[13px] text-gray-600 border-t border-gray-100">{title}</div>
      ) : null}
    </div>
  );
}
